import { useEffect, useRef } from 'react';
import { useQueries } from '@tanstack/react-query';

import { auth } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { getAllCellsForSectors } from '@api/sectors';

import { isEqual } from '@utils/common';

import { ICellForSectorDto, ICellsForSectorsDto } from '@interfaces/map/dto/sectorsDTO';

import { ETechnologyColors, TTechnologyColors } from '@enums/common';

import { TWO_MINUTES } from '@constants/common';
import { CELLS_FOR_SITE_SECTORS } from '@constants/queryKeys';

interface UseQueryCellsForSectors {
	siteIds: string[];
	isSectorsShown: boolean;
}

export interface ICellsWithColor extends ICellForSectorDto {
	color: TTechnologyColors;
}

interface UseQueryCellsForSectorsResult {
	cellsForSectors: ICellsWithColor[];
	isLoading: boolean;
}

export const useQueryCellsForSectors = (params: UseQueryCellsForSectors): UseQueryCellsForSectorsResult => {
	const {
		accessPermission: {
			networkOperatorTelecomSitesAttributeUpdater,
			networkOperatorTelecomSitesViewer,
			networkOperatorKpiViewer,
			networkOperatorKpiUpdater,
		},
	} = useAppSelector(auth);

	const { siteIds, isSectorsShown } = params;

	const isSectorsAvailable = isSectorsShown && (
		networkOperatorTelecomSitesAttributeUpdater ||
		networkOperatorTelecomSitesViewer ||
		networkOperatorKpiViewer ||
		networkOperatorKpiUpdater
	);

	const results = useQueries({
		queries: siteIds?.length
			? siteIds.map((siteDbId: string) => ({
					queryKey: [CELLS_FOR_SITE_SECTORS, siteDbId] as const,
					queryFn: async () => {
						return getAllCellsForSectors([siteDbId]);
					},
					select: (data: ICellsForSectorsDto): ICellsWithColor[] => {
						return data?.cells.map((cell) => ({
							...cell,
							color: ETechnologyColors[cell.technology.name as keyof typeof ETechnologyColors],
						}));
					},
					enabled: !!siteDbId && !!isSectorsAvailable,
					retry: 0,
					keepPreviousData: true,
					refetchOnReconnect: false,
					cacheTime: 30000,
					refetchOnWindowFocus: false,
					staleTime: TWO_MINUTES,
				}))
			: [],
	});

	const isLoading = results.some(res => res.isLoading || res.isFetching || res.isInitialLoading);

	const cellsForSectors: ICellsWithColor[] = results
		.map((result) => {
			return result.data || [];
		})
		.flat();

	const prevData = useRef<ICellsWithColor[]>([]);

	useEffect(() => {
		if (!isSectorsAvailable) {
			prevData.current = [];
		}

		if (isLoading || !isSectorsAvailable) return;

		if (!isEqual(prevData.current, cellsForSectors)) {
			prevData.current = cellsForSectors;
		}
	}, [cellsForSectors, isLoading, isSectorsAvailable]);

	return {
		cellsForSectors: isSectorsAvailable
			? isLoading ? prevData.current : cellsForSectors
			: [],
		isLoading,
	};
};
